import {
  Box,
  Divider,
  Flex,
  Text,
} from "@chakra-ui/react";
import { titleize } from "helpers/string";
import { PropsWithChildren } from "react";
import { ALL_STAGES, StageKey } from "features/Stage/constants";
import { sortActionsByTriggerDate } from "features/Resources/Shared/helpers";
import { ActionV2, ActionSummary } from "features/Resources/Shared/models";
import { stageKeyCalculator } from "features/Stage/helpers";
import StageActionCard from "../StageActionCard";

interface StageInfo {
  stage: { name: string; id: string; label: string; position: number; };
  actions: Array<ActionSummary>;
}

interface MapActionsToStages {
  actions: ActionSummary[];
}

function getStageByDate(startDate: Date, otherDate: Date) {
  return stageKeyCalculator(startDate, otherDate);
}

export function isCurrentStage(stageName: string, startDate: Date): boolean {
  const now = new Date();
  const currentStage = getStageByDate(startDate, now);
  return currentStage === stageName;
}

export function isPastStage(stageName: string, startDate: Date): boolean {
  if (!stageName) return false;

  const now = new Date();
  const currentStage = getStageByDate(startDate, now);

  const stageOrder = ALL_STAGES.map((v) => v.key);

  if (!currentStage) return false;

  const currentStageIndex = stageOrder.indexOf(currentStage);
  const targetStageIndex = stageOrder.indexOf(stageName as StageKey);

  // Special cases for stages that don't follow the timeline
  if (stageName === "immediate") {
    return true;
  }

  if (stageName === "dependency") {
    return false; // These don't have a "past" concept
  }

  // If both stages are in our order, compare their indices
  if (currentStageIndex !== -1 && targetStageIndex !== -1) {
    return targetStageIndex < currentStageIndex;
  }

  return false;
}

export function mapActionsToStages({ actions }: MapActionsToStages) {
  const stageMap: Record<string, StageInfo> = ALL_STAGES.reduce((acc, stage) => {
    acc[stage.key] = {
      stage: { name: stage.key, label: stage.label, id: stage.key, position: stage.position },
      actions: [],
    };
    return acc;
  }, {} as Record<string, StageInfo>);

  actions.forEach((action) => {
    const { stageKey } = action;
    if (!stageKey) {
      return;
    }
    if (stageMap[stageKey]) {
      stageMap[stageKey].actions.push(action);
    } else {
      stageMap[stageKey] = {
        stage: { name: stageKey, label: titleize(stageKey), id: stageKey, position: 11 },
        actions: [action],
      };
    }
  });
  return Object.values(stageMap).sort((a, b) => a.stage.position - b.stage.position);
}

function BlankCard() {
  return <Box width="250px" h="120px" />;
}

interface ReviewColumnProps {
  title: string;
}

function ReviewColumn({ title, children }: ReviewColumnProps & PropsWithChildren) {
  return (
    <Flex direction="column" align="start" width="250px" mt="2">
      <Flex align="center" gap="2">
        <Text fontSize="sm" fontWeight="bold" color="cdsGrey.700">
          {title}
        </Text>
      </Flex>
      <Divider my="2" borderColor="cdsGrey.500" borderWidth="1.25px" />
      <Flex direction="column" mt="2" gap="2">
        {children}
      </Flex>
    </Flex>
  );
}

interface DisplayActionsByStageProps {
  startDate: Date;
  actions: ActionSummary[];
  expandClickHandlerByAction: (action: ActionSummary["action"] | ActionV2) => void;
  isPending: boolean;
}

export default function DisplayActionsByStage({ startDate, actions, expandClickHandlerByAction, isPending }: DisplayActionsByStageProps) {
  const mappedStages = mapActionsToStages({
    actions: actions ?? [],
  });
  return (
    <Flex mt={4} gap="2" pb="1" minWidth="max-content">
      {(mappedStages || []).map((stageInfo) => (
        <ReviewColumn key={stageInfo.stage.id} title={stageInfo.stage.label}>
          {stageInfo.actions.length ? (
            sortActionsByTriggerDate(stageInfo.actions).map((action) => (
              <StageActionCard
                key={action?.action?.prefixId}
                action={action}
                handleClick={() => expandClickHandlerByAction(action?.action)}
                isPending={isPending}
              />
            ))
          ) : (
            <BlankCard />
          )}
        </ReviewColumn>
      ))}
    </Flex>
  );
}
